import { t } from '@/i18n';

interface ChatSettingsProps {
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
}

export function ChatSettings({ config, onConfigChange }: ChatSettingsProps) {
  const mode = (config.mode as string) ?? 'compact';
  const compactLimit = (config.compactLimit as number) ?? 5;
  const notifyMentioned = (config.notify_mentioned as boolean) ?? true;
  const notifyAuthor = (config.notify_author as boolean) ?? false;
  const notifyAllParticipants = (config.notify_all_participants as boolean) ?? false;

  const handleChange = (field: string, value: unknown) => {
    onConfigChange({ ...config, [field]: value });
  };

  return (
    <div>
      <h5>{t('dashboard.widget.chat.section.mainSettings')}</h5>

      <div className="mb-3">
        <label className="form-label">{t('dashboard.widget.chat.field.mode')}</label>
        <select
          className="form-select"
          data-testid="chat-settings-mode"
          value={mode}
          onChange={(e) => handleChange('mode', e.target.value)}
        >
          <option value="compact">{t('dashboard.widget.chat.mode.compact')}</option>
          <option value="expanded">{t('dashboard.widget.chat.mode.expanded')}</option>
        </select>
        <div className="form-text">
          {t('dashboard.widget.chat.hint.mode')}
        </div>
      </div>

      {mode === 'compact' && (
        <div className="mb-3">
          <label className="form-label">{t('dashboard.widget.chat.field.compactLimit')}</label>
          <input
            type="number"
            className="form-control"
            data-testid="chat-settings-compact-limit"
            value={compactLimit}
            min={1}
            max={10}
            onChange={(e) => handleChange('compactLimit', parseInt(e.target.value, 10) || 1)}
          />
          <div className="form-text">
            {t('dashboard.widget.chat.hint.compactLimit')}
          </div>
        </div>
      )}

      <h5 className="mt-4">{t('dashboard.widget.chat.section.notifications')}</h5>

      <div className="mb-3">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            data-testid="chat-settings-notify-mentioned"
            checked={notifyMentioned}
            onChange={(e) => handleChange('notify_mentioned', e.target.checked)}
          />
          <label className="form-check-label">
            {t('dashboard.widget.chat.field.notifyMentioned')}
          </label>
        </div>
        <div className="form-text">
          {t('dashboard.widget.chat.hint.notifyMentioned')}
        </div>
      </div>

      <div className="mb-3">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            data-testid="chat-settings-notify-author"
            checked={notifyAuthor}
            onChange={(e) => handleChange('notify_author', e.target.checked)}
          />
          <label className="form-check-label">
            {t('dashboard.widget.chat.field.notifyAuthor')}
          </label>
        </div>
        <div className="form-text">
          {t('dashboard.widget.chat.hint.notifyAuthor')}
        </div>
      </div>

      <div className="mb-3">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            data-testid="chat-settings-notify-all-participants"
            checked={notifyAllParticipants}
            onChange={(e) => handleChange('notify_all_participants', e.target.checked)}
          />
          <label className="form-check-label">
            {t('dashboard.widget.chat.field.notifyAllParticipants')}
          </label>
        </div>
        <div className="form-text">
          {t('dashboard.widget.chat.hint.notifyAllParticipants')}
        </div>
      </div>
    </div>
  );
}
